import React, { useEffect, useMemo, useState } from 'react'
import styled, { css, keyframes } from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { Navigate } from 'react-router-dom'
import qs from 'qs'
import { propOr, update, head, remove, isEmpty } from 'ramda'
import { isNotNil, isNotNilOrEmpty } from 'utils/ramda'
import { getRoleDescription } from 'utils/userRoles'
import {
  clearLatestInvitationRoutine,
  getAccreditationTypesRoutine,
  getInvitationRoutine,
  registerUserRoutine
} from 'features/auth/ducks/actions'
import {
  isUserLoggedIn,
  selectAccreditationTypes,
  selectCurrentInvitation
} from 'features/auth/ducks/selectors'
import { getUnitInvitationRoutine } from 'ducks/units/actions'
import Checkbox from 'components/atoms/Checkbox'
import AutocompleteInput from 'components/atoms/AutocompleteInput'
import Collapse from '@mui/material/Collapse'
import { PATHS, PRIVATE_PATHS } from 'utils/paths'
import BackButton from 'components/atoms/BackButton'
import PhoneField from 'components/atoms/PhoneInput'
import InputDescription from 'features/createBp/components/atoms/InputDescription'
import ExpandableAccreditations from 'features/auth/components/ExpandableAccreditations'
const IconCheck = () => (
  <span style={{ color: '#36becd', fontWeight: 'bold', fontSize: '18px' }}>✓</span>
)
const IconError = () => (
  <span style={{ color: '#cd2000', fontWeight: 'bold', fontSize: '18px' }}>!</span>
)

const IconEyeOpen = () => (
  <svg
    width='20'
    height='20'
    viewBox='0 0 24 24'
    fill='none'
    stroke='#6B7280'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <path d='M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z' />
    <circle cx='12' cy='12' r='3' />
  </svg>
)

const IconEyeClosed = () => (
  <svg
    width='20'
    height='20'
    viewBox='0 0 24 24'
    fill='none'
    stroke='#6B7280'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <path d='M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24' />
    <line x1='1' y1='1' x2='23' y2='23' />
  </svg>
)

const validateField = (name, value, allValues) => {
  switch (name) {
    case 'email':
      if (!value) return 'Email is required.'
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Invalid email format.'
      return null
    case 'password':
      if (!value) return 'Password is required.'
      if (value.length < 12) return 'Minimum 12 characters required.'
      return null
    case 'firstName':
    case 'lastName':
    case 'street':
    case 'city':
    case 'postalCode':
      return !value || value.trim().length === 0 ? 'Required.' : null
    case 'phoneNumber':
      if (!value || value.length < 5) return 'Phone number is required.'
      return null
    default:
      return null
  }
}

const emptyValues = {
  type: 'user',
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  phoneNumber: '',
  company: '',
  postalCode: '',
  street: '',
  city: '',
  disclaimer: false,
  updates: false,
  accreditations: [{ id: '', number: '' }]
}

const ModernInput = ({
                       label,
                       name,
                       value,
                       type = 'text',
                       onChange,
                       onBlur,
                       error,
                       touched,
                       required,
                       disabled,
                       optional,
                       rightElement
                     }) => {
  const hasError = touched && !!error
  const isValid = touched && !error && value && value.length > 0

  return (
    <InputGroup>
      <Label htmlFor={name}>
        {label}
        {required && <RequiredStar>*</RequiredStar>}
        {optional && <OptionalText>(Optional)</OptionalText>}
      </Label>

      <InputWrapper $hasError={hasError} $isValid={isValid} $disabled={disabled}>
        <StyledInput
          id={name}
          name={name}
          type={type}
          value={value}
          disabled={disabled}
          onChange={e => onChange(name, e.target.value)}
          onBlur={onBlur}
          autoComplete='off'
        />
        <RightSlot>
          {rightElement}
          {!rightElement && isValid && <IconCheck />}
          {hasError && <IconError />}
        </RightSlot>
      </InputWrapper>

      {hasError && <ErrorMessage>{error}</ErrorMessage>}
    </InputGroup>
  )
}

const PasswordRequirements = ({ password }) => {
  const reqs = [
    { label: 'Min. 12 characters', met: password && password.length >= 12 },
    { label: '1 digit', met: password && /\d/.test(password) },
    { label: '1 lowercase', met: password && /[a-z]/.test(password) },
    { label: '1 special character', met: password && /[!@#$%^&*(),.?":{}|<>]/.test(password) },
    { label: '1 uppercase', met: password && /[A-Z]/.test(password) }
  ]

  return (
    <ReqGrid>
      {reqs.map((r, i) => (
        <ReqItem key={i} $met={r.met}>
          <ReqDot $met={r.met} /> {r.label}
        </ReqItem>
      ))}
    </ReqGrid>
  )
}

export const AccreditationForm = ({
                                    values,
                                    options,
                                    index,
                                    onChange = () => {},
                                    onRemove = () => {},
                                    withDelete
                                  }) => {
  const selectedOption = options.find(option => option.value === values.id)

  return (
    <AccreditationFormWrapper>
      <AutocompleteInput
        name='id'
        options={options}
        withDelete={withDelete}
        onDelete={onRemove(index)}
        onChange={onChange(index)}
        inputValue={values.id}
        value={selectedOption}
        label='Type'
      />
      <Collapse in={!isEmpty(values.id)}>
        {!isEmpty(values.id) && (
          <AccreditationNumberWrapper>
            <ModernInput
              optional
              label='Number'
              name='number'
              value={values.number}
              onChange={(name, val) => onChange(index)(name, val)}
            />
          </AccreditationNumberWrapper>
        )}
      </Collapse>
    </AccreditationFormWrapper>
  )
}

const SignUp = () => {
  const dispatch = useDispatch()
  const isloggedin = useSelector(isUserLoggedIn)
  const accreditationTypes = useSelector(selectAccreditationTypes)
  const currentInvitation = useSelector(selectCurrentInvitation)

  const [values, setValues] = useState(emptyValues)
  const [errors, setErrors] = useState({})
  const [touched, setTouched] = useState({})
  const [referral, setReferral] = useState(null)
  const [showPassword, setShowPassword] = useState(false)
  const [phoneValid, setPhoneValid] = useState(false)

  const isFrs = useMemo(() => values.type === 'emergency_service', [values])
  const roleDescription = useMemo(() => getRoleDescription(values.type), [values.type])

  const options = useMemo(
    () =>
      accreditationTypes
        ? [{ label: '', value: '' }, ...accreditationTypes.map(type => ({ label: type.name, value: type.id }))]
        : [],
    [accreditationTypes]
  )

  useEffect(() => {
    const queryParams = qs.parse(window.location.search.substr(1))
    if (queryParams.invitation_id) {
      const payload = { id: queryParams.invitation_id, email: queryParams.email.replace('+', '%2B') }
      queryParams.type === 'building_passport'
        ? dispatch(getInvitationRoutine(payload))
        : dispatch(getUnitInvitationRoutine(payload))
    }
    setReferral(propOr(null, 'referral', queryParams))
    setTimeout(() => window.history.replaceState(null, null, window.location.pathname), 0)
    dispatch(getAccreditationTypesRoutine())
    return () => dispatch(clearLatestInvitationRoutine())
  }, [dispatch])

  useEffect(() => {
    if (!isEmpty(currentInvitation)) {
      setValues(prev => ({
        ...prev,
        firstName: currentInvitation.firstName,
        lastName: currentInvitation.lastName,
        email: currentInvitation.email,
        phoneNumber: currentInvitation.phoneNumber || '',
        type: 'user'
      }))
    }
  }, [currentInvitation])

  const handleOnChange = (name, value) => {
    const newValues = { ...values, [name]: value }
    setValues(newValues)

    if (touched[name]) {
      const errorMsg = validateField(name, value, newValues)
      setErrors(prev => ({ ...prev, [name]: errorMsg }))
    }
  }

  const handleBlur = name => {
    setTouched(prev => ({ ...prev, [name]: true }))
    const errorMsg = validateField(name, values[name], values)
    setErrors(prev => ({ ...prev, [name]: errorMsg }))
  }

  const handleAccreditationChange = index => (name, value) => {
    const accreditation = values.accreditations[index]
    const newValue = { ...accreditation, [name]: value }
    setValues({
      ...values,
      accreditations: update(index, newValue, values.accreditations)
    })
  }

  const handleAccreditationRemove = index => () => {
    if (values.accreditations.length > 1) {
      setValues({
        ...values,
        accreditations: remove(index, 1, values.accreditations)
      })
    } else {
      setValues({
        ...values,
        accreditations: [{ id: '', number: '' }]
      })
    }
  }

  const handleAccreditationAdd = () => {
    setValues({
      ...values,
      accreditations: [...values.accreditations, { id: '', number: '' }]
    })
  }

  const isFormValid = useMemo(() => {
    const required = ['firstName', 'lastName', 'email', 'password', 'street', 'city', 'postalCode']
    if (isFrs) required.push('company')

    const allFilled = required.every(field => isNotNilOrEmpty(values[field]))
    const consent = values.disclaimer === true
    const isPhoneOk = values.phoneNumber && values.phoneNumber.length > 5 && phoneValid
    const pwd = values.password
    const pwdOk =
      pwd.length >= 12 &&
      /\d/.test(pwd) &&
      /[a-z]/.test(pwd) &&
      /[A-Z]/.test(pwd) &&
      /[!@#$%^&*(),.?":{}|<>]/.test(pwd)

    const hasErrors = Object.values(errors).some(e => e)

    return allFilled && consent && isPhoneOk && pwdOk && !hasErrors
  }, [values, errors, isFrs, phoneValid])

  const handleSubmit = async e => {
    e.preventDefault()

    if (!isFormValid) {
      const required = ['email', 'password', 'firstName', 'lastName', 'street', 'city', 'postalCode']
      if (isFrs) required.push('company')

      const newTouched = { ...touched }
      const newErrors = { ...errors }

      required.forEach(name => {
        newTouched[name] = true
        newErrors[name] = validateField(name, values[name], values)
      })

      setTouched(newTouched)
      setErrors(newErrors)
      return
    }

    await dispatch(
      registerUserRoutine({
        ...values,
        accreditations: values.accreditations.filter(item => isNotNilOrEmpty(item.id)),
        email: values.email.toLowerCase(),
        passwordConfirmation: values.password,
        ...(referral ? { referral } : {})
      })
    )
  }

  if (isloggedin) {
    return <Navigate to={PRIVATE_PATHS.dashboard} />
  }

  return (
    <PageWrapper>
      <BackButtonWrapper>
        <BackButton to={PATHS.login}>Back to Log in</BackButton>
      </BackButtonWrapper>

      <SignupWrapper>
        <HeaderText>
          Let’s create a safer
          <span>Built Environment</span>
        </HeaderText>

        <FormDescription>
          Join Building Passport now and let’s start making the world a safer place together.
        </FormDescription>

        <SignupForm onSubmit={handleSubmit}>
          {!isNotNilOrEmpty(currentInvitation) && (
            <>
              <Checkbox
                label='I am an emergency service worker'
                name='type'
                onChange={(name, value) => handleOnChange(name, value ? 'emergency_service' : 'user')}
              />
              {roleDescription && (
                <DescriptionWrapper>
                  <InputDescription>{roleDescription}</InputDescription>
                </DescriptionWrapper>
              )}
            </>
          )}

          <SectionName>credentials</SectionName>

          <ModernInput
            required
            label='Email'
            name='email'
            value={values.email}
            error={errors.email}
            touched={touched.email}
            disabled={isNotNilOrEmpty(currentInvitation.email)}
            onChange={handleOnChange}
            onBlur={() => handleBlur('email')}
          />

          <ModernInput
            required
            type={showPassword ? 'text' : 'password'}
            label='Password'
            name='password'
            value={values.password}
            error={errors.password}
            touched={touched.password}
            onChange={handleOnChange}
            onBlur={() => handleBlur('password')}
            rightElement={
              <EyeToggle onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <IconEyeOpen /> : <IconEyeClosed />}
              </EyeToggle>
            }
          />
          <PasswordRequirements password={values.password} />

          <SectionName>personal details</SectionName>
          <SectionDescription>
            After logging in you can change Privacy Settings in &quot;My Account&quot; section and choose whether
            your personal details are shown for the public in the Building Passport or not.
          </SectionDescription>

          <TwoCols>
            <ModernInput
              required
              label='First name'
              name='firstName'
              value={values.firstName}
              error={errors.firstName}
              touched={touched.firstName}
              onChange={handleOnChange}
              onBlur={() => handleBlur('firstName')}
            />
            <ModernInput
              required
              label='Last name'
              name='lastName'
              value={values.lastName}
              error={errors.lastName}
              touched={touched.lastName}
              onChange={handleOnChange}
              onBlur={() => handleBlur('lastName')}
            />
          </TwoCols>

          <PhoneField
            name='phoneNumber'
            label='Phone number'
            value={values.phoneNumber}
            onChange={handleOnChange}
            validate={valid => setPhoneValid(valid)}
          />

          {!isFrs && !isEmpty(values.type) && (
            <ExpandableAccreditations title='Accreditations / Qualifications' optional>
              <SectionDescription>
                Please add details of any professional accreditations or qualifications you have, along with
                corresponding membership numbers if appropriate. The quality and accuracy of the information you add
                to a Building Passport should be appropriate to the professional qualifications you hold.
              </SectionDescription>
              {values.accreditations && (
                <>
                  <InputDescription>
                    If you don&apos;t see your accreditation type on the list, please{' '}
                    <a href='mailto:info@buildingpassport.com'>contact the administrator.</a>
                  </InputDescription>
                  <AccreditationsFormsWrapper>
                    {values.accreditations.map((item, index) => (
                      <AccreditationForm
                        key={`accreditation-form-${index}`}
                        index={index}
                        values={item}
                        options={options}
                        onChange={handleAccreditationChange}
                        onRemove={handleAccreditationRemove}
                        withDelete={
                          !isEmpty(head(values.accreditations).id) ||
                          values.accreditations.length > 1
                        }
                      />
                    ))}
                  </AccreditationsFormsWrapper>
                  <AddAnotherTypeButton onClick={handleAccreditationAdd}>
                    + Add another accreditation
                  </AddAnotherTypeButton>
                </>
              )}
            </ExpandableAccreditations>
          )}

          <SectionName>contact details</SectionName>

          <ModernInput
            required={isFrs}
            optional={!isFrs}
            label={isFrs ? 'FRS Station' : 'Company'}
            name='company'
            value={values.company}
            error={errors.company}
            touched={touched.company}
            onChange={handleOnChange}
            onBlur={() => handleBlur('company')}
          />
          <ModernInput
            required
            label='Postal code'
            name='postalCode'
            value={values.postalCode}
            error={errors.postalCode}
            touched={touched.postalCode}
            onChange={handleOnChange}
            onBlur={() => handleBlur('postalCode')}
          />
          <ModernInput
            required
            label='Address'
            name='street'
            value={values.street}
            error={errors.street}
            touched={touched.street}
            onChange={handleOnChange}
            onBlur={() => handleBlur('street')}
          />
          <ModernInput
            required
            label='City'
            name='city'
            value={values.city}
            error={errors.city}
            touched={touched.city}
            onChange={handleOnChange}
            onBlur={() => handleBlur('city')}
          />

          <CheckboxWrapper>
            <Checkbox
              label='I agree to Building Passport Policy & Terms of use'
              name='disclaimer'
              onChange={handleOnChange}
            />
          </CheckboxWrapper>
          <CheckboxWrapper>
            <Checkbox
              label='I want to receive marketing promotions, special offers, inspiration and policy updates via email.'
              name='updates'
              onChange={handleOnChange}
            />
          </CheckboxWrapper>

          <ButtonWrapper>
            <SubmitButton type='submit' disabled={!isFormValid}>
              Create account
            </SubmitButton>
          </ButtonWrapper>
        </SignupForm>
      </SignupWrapper>
    </PageWrapper>
  )
}

export default SignUp

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
`

const PageWrapper = styled.div`
  box-sizing: border-box;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.backgroundColor};
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 16px;
  min-height: ${({ theme }) => `calc(100vh - ${theme.dimensions.topBarHeight})`};
  overflow-y: auto;

  @media (max-width: 768px) {
    padding: 24px 12px 32px;
    min-height: 100vh;
  }
`

const BackButtonWrapper = styled.div`
  width: 100%;
  max-width: 645px;
  margin: 0 auto 15px;
`

const SignupWrapper = styled.div`
  box-sizing: border-box;
  width: 100%;
  max-width: 645px;
  background-color: ${({ theme }) => theme.colors.white};
  padding: 50px 160px;
  border-radius: 8px;
  box-shadow: 0 0 10px rgba(93, 117, 208, 0.1);

  @media (max-width: 900px) {
    padding: 40px 60px;
  }

  @media (max-width: 600px) {
    padding: 32px 20px;
    border-radius: 12px;
  }
`

const FormDescription = styled.div`
  font-size: 16px;
  line-height: 26px;
  color: ${({ theme }) => theme.colors.haiti};
  opacity: 0.7;
  margin: 25px 0 30px;
`

const HeaderText = styled.h1`
  font-size: 38px;
  margin-bottom: 30px;

  span {
    display: block;
    font-weight: bold;
  }

  @media (max-width: 600px) {
    font-size: 30px;
  }
`

const SignupForm = styled.form`
  box-sizing: border-box;
  width: 100%;
`

const ButtonWrapper = styled.div`
  width: 100%;
  text-align: right;
  margin-top: 30px;
`

const CheckboxWrapper = styled.div`
  width: 100%;
  padding: 5px 0;
  line-height: 30px;

  a {
    text-decoration: underline;
  }
`

const SectionName = styled.div`
  text-transform: uppercase;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.primary};
  letter-spacing: 0.1px;
  margin: 20px 0 10px;

  span {
    color: ${({ theme }) => theme.colors.grey[700]};
    text-transform: lowercase;
  }
`

const SectionDescription = styled.div`
  font-size: 12px;
  margin-bottom: 15px;
`

const AccreditationFormWrapper = styled.div`
  &:not(:last-of-type) {
    margin-bottom: 15px;
  }

  @media (max-width: 768px) {
    width: 100%;
    box-sizing: border-box;
    padding: 0;
    margin-bottom: 16px;

    &:not(:last-of-type) {
      margin-bottom: 16px;
    }
  }
`

const AccreditationNumberWrapper = styled.div`
  background-color: rgba(247, 248, 253, 0.51);
  border: 1px solid #e9eff4;
  padding: 15px 15px 0;
`

const AddAnotherTypeButton = styled.div`
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.secondary};
  font-size: 12px;
  font-weight: bold;
  width: fit-content;
  cursor: pointer;
  margin: 10px 0 20px auto;

  ${({ disabled, theme }) =>
    disabled &&
    css`
      color: ${theme.colors.grey[600]};
      cursor: default;
    `}
`

const AccreditationsFormsWrapper = styled.div`
  margin-top: 10px;
`

const DescriptionWrapper = styled.div`
  margin: 20px 0;
`

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 18px;
  width: 100%;
`

const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-left: 2px;
  display: flex;
  align-items: center;
  gap: 4px;
`

const RequiredStar = styled.span`
  color: #cd2000;
`

const OptionalText = styled.span`
  font-weight: 400;
  color: #9ca3af;
  font-size: 11px;
`

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  background: #fff;
  border: 1px solid
  ${props => (props.$hasError ? '#cd2000' : props.$isValid ? '#36becd' : '#cdd4ea')};
  border-radius: 6px;
  transition: all 0.2s ease;
  padding: 0 12px;
  height: 44px;
  box-shadow: ${props => (props.$hasError ? '0 0 0 3px rgba(205, 32, 0, 0.1)' : 'none')};

  &:focus-within {
    border-color: ${props => (props.$hasError ? '#cd2000' : '#36becd')};
    box-shadow: 0 0 0 3px
    ${props => (props.$hasError ? 'rgba(205, 32, 0, 0.1)' : 'rgba(54, 190, 205, 0.1)')};
  }

  ${props =>
    props.$disabled &&
    css`
      background: #f3f4f6;
      cursor: not-allowed;
    `}
`

const StyledInput = styled.input`
  border: none;
  background: transparent;
  width: 100%;
  height: 100%;
  font-size: 14px;
  color: #111827;
  outline: none;

  &::placeholder {
    color: #9ca3af;
  }
`

const RightSlot = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
  flex-shrink: 0;
`

const EyeToggle = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  opacity: 0.6;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }
`

const ErrorMessage = styled.div`
  font-size: 12px;
  color: #cd2000;
  margin-top: 4px;
  animation: ${fadeIn} 0.2s;
`

const TwoCols = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;

  > div {
    flex: 1;
  }

  @media (max-width: 600px) {
    flex-direction: column;
  }
`

const ReqGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  margin-top: 6px;
`

const ReqItem = styled.div`
  font-size: 11px;
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${props => (props.$met ? '#111827' : '#9ca3af')};
`

const ReqDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => (props.$met ? '#36becd' : '#f3f4f6')};
`
const SubmitButton = styled.button`
  width: 100%;
  padding: 14px;
  border-radius: 6px;
  border: 1px solid #36becd;
  background-color: #36becd;
  color: #ffffff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:enabled {
    background-color: #0c132b;
    border-color: #0c132b;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(12, 19, 43, 0.2);
  }

  &:disabled {
    background-color: #d1d5db;
    border-color: #d1d5db;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`
